import {Stack, Space} from '@mantine/core';
import {Overview} from "./Overview";
import {PageTitleText} from "./PageTitleText";
import {NormalText} from "./NormalText";
import {CommandNameText} from './CommandNameText';
import {PlainTextCodeBox} from "./PlainTextCodeBox";

const configExample = `
[jellyfish]
hash_size = 1_000_000
threads = 12
kmer_length = 8

[download]
accessions = ['DRR259113', 'SRR6670338']

[cutadapt]
-a = ["ATTAA", "TTTCTT"]
-g = "^ATTTCCC"
--cores = 10
--quiet = ""

[cgr]
size = 64
log10 = true
normalized = true

[files]
fastq = false
counts = false
trim = false
`;

const hash = `
hash_size = 1_000_000
`;

const threads = `
threads = 10
`;

const kmerLength = `
kmer_length = 8
`;

const accessionList = `
accessions = ['DRR259113', 'SRR6670338']
`;

const adapterStart = `
-a = ["ATTAA", "TTTCTT"]
`;

const adapterEnd = `
-g = "^ATTTCCC"
`;

const cores = `
--cores = 10
`;

const quietMode = `
--quiet = ""
`;

const imgSize = `
size = 64
`;

const log10 = `
log10 = true
`;

const normalization = `
normalized = true
`;

const fastq = `
fastq = false
`;
const counts = `
counts = false
`;

const trim = `
trim = false
`;

export function ConfigTextSection() {
    return (
        <Stack
            justify="flex-start"
            align="flex-start"
            gap="xl"
            mr="xl"
            ml="xl"
            mb="xl"
            maw="60rem"
        >
            <Stack
                gap="xs"
                justify="flex-start">

                <Overview/>

                <PageTitleText>
                    Config File
                </PageTitleText>

                <NormalText>
                    The config file allows users to set up various parameters to control how the CLI tool operates.
                    This file helps avoid specifying options via the command line repeatedly, offering more control over
                    the workflow.
                    The config file is structured in sections and follows a standard format for keys and values.
                    Each section of the config file corresponds to different aspects of the process (Jellyfish args,
                    download args, etc.).
                </NormalText>
            </Stack>

            <Stack gap={'m'} id='file_structure'>
                <CommandNameText>
                    File Structure
                </CommandNameText>

                <NormalText>
                    The config file uses the TOML format with different sections represented in square brackets [].
                    Each section contains key-value pairs that define the parameters to be used in the process.
                </NormalText>

                <NormalText>
                    - If invalid keys are present in the config file, the program will throw an error and exit.<br/>
                    - Default values will be used if optional parameters are not provided.
                </NormalText>

                <NormalText>
                    The following is an example config file with all keys:
                </NormalText>
                <PlainTextCodeBox>{configExample}</PlainTextCodeBox>
            </Stack>
            <Space h="s" />
            <Stack gap={'m'} id='jellyfish_args'>
                <CommandNameText>
                    Jellyfish Arguments
                </CommandNameText>

                <NormalText>
                    This section defines the parameters passed to Jellyfish [jellyfish], which is used for k-mer
                    counting.
                </NormalText>

                <NormalText>
                    <strong>hash_size</strong>: The number of kmers stored in the hash.<br/>
                    <i>Type</i>: Integer<br/>
                    <i>Default</i>: 100_000_000
                </NormalText>

                <NormalText>
                    Example with hash size of 1,000,000:
                </NormalText>
                <PlainTextCodeBox>{hash}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>threads</strong>: The number of threads Jellyfish will use.<br/>
                    <i>Type</i>: Integer<br/>
                    <i>Default</i>: 10
                </NormalText>

                <NormalText>
                    Example with 10 threads:
                </NormalText>
                <PlainTextCodeBox>{threads}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>kmer_length</strong>: The length of k in the kmers counted.<br/>
                    <i>Type</i>: Integer<br/>
                    <i>Required</i>: Yes
                </NormalText>

                <NormalText>
                    Example with kmer length of 8:
                </NormalText>
                <PlainTextCodeBox>{kmerLength}</PlainTextCodeBox>
            </Stack>
            <Space h="s" />
            <Stack gap={'m'} id='download_args'>
                <CommandNameText>
                    Download Arguments
                </CommandNameText>

                <NormalText>
                    This section specifies the accession numbers used for downloading genome data [download].
                </NormalText>

                <NormalText>
                    <strong>accessions</strong>: A list of accession numbers for which to generate images.<br/>
                    <i>Type</i>: List of strings <i>or</i> a file path string<br/>
                    <i>Required</i>: Yes
                </NormalText>

                <NormalText>
                    Example with accession numbers DRR259113 and SRR6670338:
                </NormalText>
                <PlainTextCodeBox>{accessionList}</PlainTextCodeBox>
            </Stack>
            <Space h="s" />
            <Stack gap={'m'} id='cutadapt_args'>
                <CommandNameText>
                    Cutadapt Arguments
                </CommandNameText>

                <NormalText>
                    Cutadapt [cutadapt] is used to trim fastq files based on adapter sequences.
                </NormalText>

                <NormalText>
                    <strong>-a</strong>: The adapter sequence(s) to trim from the start of reads.<br/>
                    <i>Type</i>: List of strings<br/>
                </NormalText>

                <NormalText>
                    Example with adapter sequences ATTAA and TTTCTT:
                </NormalText>
                <PlainTextCodeBox>{adapterStart}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>-g</strong>: The adapter sequence(s) to trim from the end of reads.<br/>
                    <i>Type</i>: String<br/>
                </NormalText>

                <NormalText>
                    Example with adapter sequence ^ATTTCCC:
                </NormalText>
                <PlainTextCodeBox>{adapterEnd}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>--cores</strong>: Number of CPU cores to use for trimming.<br/>
                    <i>Type</i>: Integer<br/>
                </NormalText>

                <NormalText>
                    Example with 10 cores:
                </NormalText>
                <PlainTextCodeBox>{cores}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>--quiet</strong>: Suppresses detailed output from Cutadapt.<br/>
                    <i>Type</i>: None (leave value as empty string "")<br/>
                </NormalText>

                <NormalText>
                    Example with quiet mode enabled:
                </NormalText>
                <PlainTextCodeBox>{quietMode}</PlainTextCodeBox>
            </Stack>
            <Space h="s" />
            <Stack gap={'m'} id='cgr_args'>
                <CommandNameText>
                    CGR Arguments
                </CommandNameText>

                <NormalText>
                    This section sets options for creating CGR (Chaos Game Representation) images.
                </NormalText>

                <NormalText>
                    <strong>size</strong>: Specifies the size of the image.<br/>
                    <i>Type</i>: Integer<br/>
                    <i>Default</i>: 2^k
                </NormalText>

                <NormalText>
                    Example with image size of 64:
                </NormalText>
                <PlainTextCodeBox>{imgSize}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>log10</strong>: Boolean value indicating whether to use log10 scaling.<br/>
                    <i>Type</i>: Boolean<br/>
                    <i>Default</i>: true
                </NormalText>

                <NormalText>
                    Example with log10 scaling enabled:
                </NormalText>
                <PlainTextCodeBox>{log10}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>normalized</strong>: Boolean value indicating whether to normalize the image.<br/>
                    <i>Type</i>: Boolean<br/>
                    <i>Default</i>: true
                </NormalText>

                <NormalText>
                    Example with image normalization enabled:
                </NormalText>
                <PlainTextCodeBox>{normalization}</PlainTextCodeBox>
            </Stack>
            <Space h="s" />
            <Stack gap={'m'} id='files_args'>
                <CommandNameText>
                    File Arguments
                </CommandNameText>

                <NormalText>
                    This section [files] controls which temporary files are kept after the process finishes.
                </NormalText>

                <NormalText>
                    <strong>fastq</strong>: If set to true, retains the FASTQ file after trimming or k-mer
                    counting.<br/>
                    <i>Type</i>: Boolean<br/>
                    <i>Default</i>: false
                </NormalText>

                <NormalText>
                    Example with FASTQ file retention enabled:
                </NormalText>
                <PlainTextCodeBox>{fastq}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>counts</strong>: If set to true, retains the counts file after generating a CGR image.<br/>
                    <i>Type</i>: Boolean<br/>
                    <i>Default</i>: false
                </NormalText>

                <NormalText>
                    Example with counts file retention enabled:
                </NormalText>
                <PlainTextCodeBox>{counts}</PlainTextCodeBox>
                <br />

                <NormalText>
                    <strong>trim</strong>: If set to true, retains the trimmed FASTQ file after being processed by
                    Jellyfish.<br/>
                    <i>Type</i>: Boolean<br/>
                    <i>Default</i>: false
                </NormalText>

                <NormalText>
                    Example with trimmed FASTQ file retention enabled:
                </NormalText>
                <PlainTextCodeBox>{trim}</PlainTextCodeBox>

                <Space h='xl'/>
            </Stack>
        </Stack>
    );
}